// ===========================================
// konakla - Veritabani <-> Frontend Tip Donusumleri
// ===========================================

import type { Listing as DBListingRow } from "@/types/database";
import type { Listing } from "@/types";
import { normalizePricingRules } from "@/lib/utils/pricing-rules";

// Kapak görselini dizinin başına alır (kart/galeri images[0]'ı kapak olarak gösterir).
// Kapak dizide yoksa veya yoksa dizi olduğu gibi döner.
function orderImagesByCover(images: string[] | null, cover: string | null): string[] {
  const arr = images ?? [];
  if (!cover || !arr.includes(cover)) return arr;
  return [cover, ...arr.filter((img) => img !== cover)];
}

// Veritabani satirini (snake_case) frontend tipine (camelCase) donusturur.
// Owner verisi opsiyoneldir - join ile gelen sorgularda dolacaktir.
//
// `İÇ_KOLONLAR` neden Partial: herkese açık sorgular artık `select("*")`
// kullanmıyor; ilanın yalnızca güvenli kolonları çekiliyor
// (bkz. lib/db/public-fields.ts — `ical_url` ev sahibinin gizli Airbnb takvim
// linkiydi ve kimliksiz herkese dönüyordu). Bu fonksiyon o kolonların
// yalnızca birini okuyor (`tourism_license_document`) ve hedef alan zaten
// opsiyonel. Tipi gevşetmek `as any` cast'inden ÇOK daha iyi: eksik olabilecek
// alanlar açıkça yazılı, gerisi hâlâ zorunlu.
type ICListingKolonlari =
  | "ical_url"
  | "ical_last_sync"
  | "admin_notes"
  | "rejection_reason"
  | "moderated_by"
  | "moderated_at"
  | "auto_deactivated_at"
  | "tourism_license_document"
  | "featured_until";

type DBListingWithOptionalOwner = Omit<DBListingRow, ICListingKolonlari> &
  Partial<Pick<DBListingRow, ICListingKolonlari>> & {
    owner?: {
      id: string;
      name: string;
      surname: string | null;
      email: string | null;
      phone: string | null;
      whatsapp: string | null;
      website: string | null;
      profile_image: string | null;
      description: string | null;
    } | null;
  };

export function transformListing(dbListing: DBListingWithOptionalOwner): Listing {
  return {
    id: dbListing.id,
    slug: dbListing.slug,
    title: dbListing.title,
    description: dbListing.description,
    propertyType: dbListing.property_type,
    status: dbListing.status,
    capacity: dbListing.capacity,
    bedrooms: dbListing.bedrooms,
    bathrooms: dbListing.bathrooms,
    beds: dbListing.beds,
    location: {
      city: dbListing.city,
      district: dbListing.district,
      neighborhood: dbListing.neighborhood ?? undefined,
      address: dbListing.address ?? undefined,
      latitude: dbListing.latitude ?? undefined,
      longitude: dbListing.longitude ?? undefined,
    },
    // Kapak görseli her zaman ilk sırada — kart/galeri images[0]'ı gösterir.
    images: orderImagesByCover(dbListing.images, dbListing.cover_image),
    coverImage: dbListing.cover_image,
    priceInfo: {
      nightlyPrice: dbListing.nightly_price,
      currency: dbListing.currency,
      minimumStay: dbListing.minimum_stay || 1,
      cleaningFee: dbListing.cleaning_fee ?? undefined,
      // pricing_rules DB'de { rules: [...] } objesi; düz diziye normalize edilir.
      pricingRules: normalizePricingRules(dbListing.pricing_rules),
    },
    amenities: (dbListing.amenities as Record<string, boolean>) || {},
    customAmenities: dbListing.custom_amenities ?? undefined,
    houseRules: {
      smokingAllowed: dbListing.smoking_allowed || false,
      petsAllowed: dbListing.pets_allowed || false,
      partiesAllowed: dbListing.parties_allowed || false,
      checkInTime: dbListing.check_in_time || "14:00",
      checkOutTime: dbListing.check_out_time || "10:00",
      additionalRules: dbListing.additional_rules ?? undefined,
    },
    cancellationPolicy: dbListing.cancellation_policy ?? undefined,
    tourismLicenseNumber: dbListing.tourism_license_number ?? undefined,
    tourismLicenseDocument: dbListing.tourism_license_document ?? undefined,
    ownerId: dbListing.owner_id,
    owner: dbListing.owner
      ? {
          id: dbListing.owner.id,
          name: dbListing.owner.name,
          surname: dbListing.owner.surname ?? undefined,
          email: dbListing.owner.email ?? undefined,
          phone: dbListing.owner.phone ?? undefined,
          whatsapp: dbListing.owner.whatsapp ?? undefined,
          website: dbListing.owner.website ?? undefined,
          profileImage: dbListing.owner.profile_image ?? undefined,
          description: dbListing.owner.description ?? undefined,
        }
      : undefined,
    viewCount: dbListing.view_count || 0,
    contactClickCount: dbListing.contact_click_count || 0,
    createdAt: new Date(dbListing.created_at),
    updatedAt: new Date(dbListing.updated_at),
  };
}

export function transformListings(dbListings: DBListingWithOptionalOwner[]): Listing[] {
  return dbListings.map(transformListing);
}
